import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DateField from "../datepicker/DateField";

/**
 * SCHEDULE — the 7×24 grid, and the two rules that sit over it.
 *
 * THE GRID IS INVERTED ON PURPOSE, which is the mockup's annotation and the
 * thing to understand before reading anything else here: you paint the hours
 * the BOT is armed, not the hours you are working. The default is therefore
 * mostly-on — Sun–Thu evenings and overnight, all of Friday and Saturday.
 *
 * QUIET HOURS ARE DRAWN OVER THE TOP AS HATCHING, and that is load-bearing
 * rather than decorative. They mute the send without un-arming the hour (see
 * the header of src/automations/schedule.js for why), so an hour can be armed
 * AND muted, and the grid has to be able to say so. Two rules that could
 * disagree invisibly is exactly what the hatch prevents.
 *
 * SUNDAY FIRST. The Israeli week starts on Sunday and this grid is about when
 * Gabriel is away from his desk; a Monday-first grid would split his working
 * week across both edges.
 *
 *
 * DRAG AND KEYBOARD ARE THE SAME OPERATION.
 *
 * Painting is: pick a mode from the FIRST cell touched (if it was armed, you
 * are erasing; if it was not, you are arming) and apply that mode to everything
 * the gesture covers. That is what makes a drag across a mixed row do something
 * predictable instead of inverting each cell it passes.
 *
 * The keyboard gets the same thing rather than a lesser version: arrows move a
 * roving focus, space toggles, and shift+arrow extends the paint in the
 * direction you are moving using the mode of the cell you started from. A grid
 * that could only be operated by mouse would make the armed hours — the single
 * most consequential setting here — unreachable without one.
 */

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const HOLIDAY_PRESETS = [
  { date: "2026-09-11", label: "Rosh Hashanah" },
  { date: "2026-09-12", label: "Rosh Hashanah" },
  { date: "2026-09-13", label: "Rosh Hashanah" },
  { date: "2026-09-20", label: "Yom Kippur" },
  { date: "2026-09-21", label: "Yom Kippur" },
  { date: "2026-09-25", label: "Sukkot" },
  { date: "2026-09-26", label: "Sukkot" },
  { date: "2026-10-02", label: "Shemini Atzeret" },
  { date: "2026-10-03", label: "Shemini Atzeret" },
  { date: "2027-04-21", label: "Passover (eve)" },
  { date: "2027-04-22", label: "Passover (first day)" },
];

function pad(value) {
  return String(value).padStart(2, "0");
}

/** Is this wall-clock hour inside the quiet window? Mirrors hourInQuietWindow. */
function hourIsQuiet(hour, quiet) {
  if (!quiet || !quiet.enabled) return false;
  const start = Number(String(quiet.start).slice(0, 2));
  const end = Number(String(quiet.end).slice(0, 2));
  if (start === end) return false;
  return start > end ? hour >= start || hour < end : hour >= start && hour < end;
}

export default function ScheduleTab({ config, patch }) {
  const schedule = config.schedule;
  const grid = schedule.grid;

  // The paint gesture. `mode` is what we are doing (arm or erase), decided by
  // the first cell; `origin` is where it started, which shift+arrow extends from.
  const painting = useRef(null);
  const [focus, setFocus] = useState({ day: 0, hour: 19 });
  const cellRefs = useRef({});

  const setCells = useCallback(
    (cells, value) => {
      const next = grid.map((row) => row.slice());
      for (const { day, hour } of cells) next[day][hour] = value;
      patch("schedule", { grid: next });
    },
    [grid, patch]
  );

  const beginPaint = (day, hour) => {
    const mode = !grid[day][hour];
    painting.current = { mode, origin: { day, hour } };
    setCells([{ day, hour }], mode);
  };

  const extendPaint = (day, hour) => {
    if (!painting.current) return;
    setCells([{ day, hour }], painting.current.mode);
  };

  // Released anywhere, not just over the grid: a drag that ends outside the
  // table would otherwise leave the gesture live and paint on the next hover.
  useEffect(() => {
    const stop = () => {
      painting.current = null;
    };
    window.addEventListener("mouseup", stop);
    window.addEventListener("blur", stop);
    return () => {
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("blur", stop);
    };
  }, []);

  const move = (day, hour, event) => {
    const next = {
      day: Math.max(0, Math.min(6, day)),
      hour: Math.max(0, Math.min(23, hour)),
    };
    setFocus(next);
    const ref = cellRefs.current[`${next.day}:${next.hour}`];
    if (ref) ref.focus();
    // Shift+arrow paints as it goes, using the mode of the cell it left, which
    // is the keyboard's version of a drag.
    if (event.shiftKey) setCells([next], !!grid[day][hour]);
  };

  const onKeyDown = (event, day, hour) => {
    const keys = {
      ArrowLeft: [day - 1, hour],
      ArrowRight: [day + 1, hour],
      ArrowUp: [day, hour - 1],
      ArrowDown: [day, hour + 1],
    };
    if (keys[event.key]) {
      event.preventDefault();
      move(keys[event.key][0], keys[event.key][1], event);
      return;
    }
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      setCells([{ day, hour }], !grid[day][hour]);
    }
  };

  const summary = useMemo(() => {
    let armed = 0;
    let muted = 0;
    grid.forEach((row) =>
      row.forEach((on, hour) => {
        if (!on) return;
        armed++;
        if (hourIsQuiet(hour, schedule.quiet_hours)) muted++;
      })
    );
    return { armed, muted };
  }, [grid, schedule.quiet_hours]);

  const setWholeColumn = (day) => {
    // Every hour of a day, on or off, from whatever the column mostly is. The
    // weekend is armed end to end by default, and clicking its header is how
    // you say that in one gesture rather than twenty-four.
    const allOn = grid[day].every(Boolean);
    const next = grid.map((row, index) => (index === day ? row.map(() => !allOn) : row.slice()));
    patch("schedule", { grid: next });
  };

  return (
    <div className="crm-auto-schedule">
      <section className="crm-card crm-auto-grid-card">
        <header className="crm-auto-sectionhead">
          <h2>When it's armed</h2>
          <p className="crm-hint">
            Paint the hours nobody is around to reply by hand. Click or drag, or use the arrow keys and space.
          </p>
          <p className="crm-hint">
            Times are {schedule.timezone.replace("_", " ")}, which is where your calendar is.
          </p>
        </header>

        <div className="crm-auto-legend">
          <span className="crm-auto-legend__item">
            <i className="crm-auto-cell is-armed" /> armed
          </span>
          <span className="crm-auto-legend__item">
            <i className="crm-auto-cell is-armed is-quiet" /> armed, but quiet hours win
          </span>
          <span className="crm-auto-legend__item">
            <i className="crm-auto-cell" /> you reply yourself
          </span>
        </div>

        <div className="crm-auto-grid" role="group" aria-label="Armed hours, by day and hour">
          <div className="crm-auto-grid__head">
            <span />
            {DAY_LABELS.map((label, day) => (
              <button
                key={label}
                type="button"
                /* Friday and Saturday read blue because the whole column is
                   armed by default — the mockup's cheap way to see the weekend
                   at a glance. Derived, so it stays true if he repaints. */
                className={`crm-auto-grid__day${grid[day].every(Boolean) ? " is-all" : ""}`}
                onClick={() => setWholeColumn(day)}
                title={`${grid[day].every(Boolean) ? "Clear" : "Arm"} all of ${label}`}
              >
                {label}
              </button>
            ))}
          </div>

          {HOURS.map((hour) => (
            <div className="crm-auto-grid__row" key={hour}>
              <span className="crm-auto-grid__hour crm-num">{pad(hour)}:00</span>
              {DAY_LABELS.map((label, day) => {
                const armed = !!grid[day][hour];
                const quiet = armed && hourIsQuiet(hour, schedule.quiet_hours);
                const isFocus = focus.day === day && focus.hour === hour;
                return (
                  <button
                    key={`${day}:${hour}`}
                    ref={(node) => {
                      cellRefs.current[`${day}:${hour}`] = node;
                    }}
                    type="button"
                    // Roving tabindex: one stop for the whole grid, then arrows.
                    // 168 tab stops would make the rest of the page unreachable.
                    tabIndex={isFocus ? 0 : -1}
                    aria-pressed={armed}
                    aria-label={`${label} ${pad(hour)}:00${armed ? ", armed" : ""}${quiet ? ", muted by quiet hours" : ""}`}
                    className={`crm-auto-cell${armed ? " is-armed" : ""}${quiet ? " is-quiet" : ""}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      setFocus({ day, hour });
                      beginPaint(day, hour);
                    }}
                    onMouseEnter={() => extendPaint(day, hour)}
                    onFocus={() => setFocus({ day, hour })}
                    onKeyDown={(event) => onKeyDown(event, day, hour)}
                  />
                );
              })}
            </div>
          ))}
        </div>

        <footer className="crm-auto-grid__foot">
          <span>
            <b className="crm-num">{summary.armed}</b> of 168 hours armed each week
            {summary.muted > 0 && (
              <>
                {" · "}
                <b className="crm-num">{summary.muted}</b> of them muted by quiet hours
              </>
            )}
          </span>
          <span className="crm-auto-grid__acts">
            <button type="button" className="crm-linkact" onClick={() => patch("schedule", { grid: defaultGrid() })}>
              Reset to default
            </button>
            <button
              type="button"
              className="crm-linkact"
              onClick={() => patch("schedule", { grid: grid.map((row) => row.map(() => false)) })}
            >
              Clear
            </button>
          </span>
        </footer>
      </section>

      <div className="crm-auto-schedule__side">
        <section className="crm-card">
          <h3>Wait before sending</h3>
          <div className="crm-auto-waitrow">
            <span>Random delay between</span>
            <input
              className="crm-input crm-input--narrow crm-num"
              type="number"
              min="0"
              value={schedule.wait_min_minutes}
              onChange={(event) => patch("schedule", { wait_min_minutes: Number(event.target.value) })}
              aria-label="Shortest wait in minutes"
            />
            <span>and</span>
            <input
              className="crm-input crm-input--narrow crm-num"
              type="number"
              min="0"
              value={schedule.wait_max_minutes}
              onChange={(event) => patch("schedule", { wait_max_minutes: Number(event.target.value) })}
              aria-label="Longest wait in minutes"
            />
            <span>minutes</span>
          </div>
          <p className="crm-hint">
            A reply that lands four seconds after they hit send reads as a bot. A few minutes reads as somebody who
            was at their desk.
          </p>
          {schedule.wait_min_minutes > schedule.wait_max_minutes && (
            <p className="crm-hint crm-hint--warn">The shortest wait has to be less than the longest.</p>
          )}
        </section>

        <section className="crm-card">
          <header className="crm-auto-cardhead">
            <h3>Quiet hours</h3>
            <label className="crm-auto-switch">
              <input
                type="checkbox"
                checked={schedule.quiet_hours.enabled}
                onChange={(event) =>
                  patch("schedule", { quiet_hours: { ...schedule.quiet_hours, enabled: event.target.checked } })
                }
              />
              <span className="crm-hint">on</span>
            </label>
          </header>

          <div className="crm-auto-waitrow">
            <span>Never send between</span>
            <input
              className="crm-input crm-input--narrow crm-num"
              type="time"
              value={schedule.quiet_hours.start}
              onChange={(event) =>
                patch("schedule", { quiet_hours: { ...schedule.quiet_hours, start: event.target.value } })
              }
              aria-label="Quiet hours start"
            />
            <span>and</span>
            <input
              className="crm-input crm-input--narrow crm-num"
              type="time"
              value={schedule.quiet_hours.end}
              onChange={(event) =>
                patch("schedule", { quiet_hours: { ...schedule.quiet_hours, end: event.target.value } })
              }
              aria-label="Quiet hours end"
            />
          </div>
          {/*
            The exact behaviour, said plainly, because it is the one thing on
            this page that surprises people: quiet hours do not turn the
            automation off, they move the send.
          */}
          <p className="crm-hint">
            Leads that arrive in this window still get an answer — it goes out at {schedule.quiet_hours.end} plus the
            random delay, rather than not at all. The grid shows these hours hatched.
          </p>
        </section>

        <Holidays
          holidays={schedule.holidays}
          onChange={(holidays) => patch("schedule", { holidays })}
        />
      </div>
    </div>
  );
}

function Holidays({ holidays, onChange }) {
  const [draft, setDraft] = useState("");
  const missing = HOLIDAY_PRESETS.filter((preset) => !holidays.includes(preset.date));

  return (
    <section className="crm-card crm-auto-holidays">
      <header className="crm-auto-cardhead">
        <h3>Holidays</h3>
        <span className="crm-hint">armed all day</span>
      </header>

      {holidays.length ? (
        <ul className="crm-auto-holidaylist">
          {[...holidays].sort().map((date) => (
            <li key={date}>
              <span>{HOLIDAY_PRESETS.find((preset) => preset.date === date)?.label || "Holiday"}</span>
              <span className="crm-num">{date}</span>
              <button
                type="button"
                onClick={() => onChange(holidays.filter((d) => d !== date))}
                aria-label={`Remove ${date}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="crm-hint">No holidays yet, so the weekly grid decides every day.</p>
      )}

      <div className="crm-auto-waitrow">
        <DateField value={draft} onChange={setDraft} aria-label="Add a holiday date" placeholder="Pick a holiday" />
        <button
          type="button"
          className="crm-btn crm-btn--secondary"
          disabled={!draft || holidays.includes(draft)}
          onClick={() => {
            onChange([...holidays, draft]);
            setDraft("");
          }}
        >
          Add
        </button>
      </div>

      {/*
        A LIST, NOT A RECURRENCE RULE, and the button says which dates it is
        adding. The Jewish calendar does not map onto a cron expression — Rosh
        Hashanah is a different Gregorian date every year — so a rule here would
        be a lie that drifts. Presets are a convenience with an expiry, and when
        they run out the answer is to add next year's, not to guess them.
      */}
      {!!missing.length && (
        <button
          type="button"
          className="crm-linkact"
          onClick={() => onChange([...holidays, ...missing.map((preset) => preset.date)])}
        >
          + Add the {missing.length} Israeli holidays through {missing[missing.length - 1].date.slice(0, 4)}
        </button>
      )}
    </section>
  );
}

/** The shipped default, duplicated here only so "reset" has something to reset to. */
function defaultGrid() {
  return Array.from({ length: 7 }, (_, day) =>
    Array.from({ length: 24 }, (_, hour) => day === 5 || day === 6 || hour < 8 || hour >= 19)
  );
}
